import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { Button, TextField } from "@yoast/ui-library";
import { doRequest, prepareInternalRequest } from "shared-frontend/functions/api";
import ErrorDisplay from "../../../../errors/ErrorDisplay";
import useRequest from "../../../../reactHooks/useRequest";
import PasskeyRow from "./PasskeyRow";
import registerPasskey, { TotpRequiredError } from "shared-frontend/functions/passkeys";

const messages = defineMessages( {
	addPasskey: {
		id: "passkeys.add",
		defaultMessage: "Add passkey",
	},
	noPasskeys: {
		id: "passkeys.empty",
		defaultMessage: "You haven't registered any passkeys yet. Passkeys let you sign in securely without a password.",
	},
	passkeyName: {
		id: "passkeys.name",
		defaultMessage: "Passkey name",
	},
	passwordLabel: {
		id: "passkeys.password",
		defaultMessage: "Confirm your password",
	},
	totpLabel: {
		id: "passkeys.totp",
		defaultMessage: "Verification code",
	},
	registering: {
		id: "passkeys.registering",
		defaultMessage: "Registering...",
	},
	confirm: {
		id: "passkeys.confirm",
		defaultMessage: "Continue",
	},
	cancel: {
		id: "passkeys.cancel",
		defaultMessage: "Cancel",
	},
	intro: {
		id: "passkeys.confirmIntro",
		defaultMessage: "Enter your password to authorize adding a new passkey.",
	},
} );

/**
 * The "add a passkey" form. Split out of PasskeyManagement so the parent's render stays simple.
 *
 * @param {Object} props The component props.
 *
 * @returns {ReactElement} The registration form.
 */
const PasskeyRegistrationForm = ( {
	intl,
	name,
	password,
	totpCode,
	requireTotp,
	registering,
	onName,
	onPassword,
	onTotpCode,
	onSubmit,
	onCancel,
} ) => (
	<form onSubmit={ onSubmit } className="yst-mt-4 yst-space-y-3">
		<p><FormattedMessage { ...messages.intro } /></p>
		<TextField
			id="passkey-name"
			name="passkey-name"
			type="text"
			label={ intl.formatMessage( messages.passkeyName ) }
			value={ name }
			onChange={ onName }
		/>
		<TextField
			id="passkey-password"
			name="passkey-password"
			type="password"
			autoComplete="current-password"
			label={ intl.formatMessage( messages.passwordLabel ) }
			value={ password }
			onChange={ onPassword }
		/>
		{ requireTotp && (
			<TextField
				id="passkey-totp"
				name="passkey-totp"
				type="text"
				inputmode="numeric"
				autoComplete="one-time-code"
				label={ intl.formatMessage( messages.totpLabel ) }
				value={ totpCode }
				onChange={ onTotpCode }
			/>
		) }
		<div className="yst-flex yst-gap-2">
			<Button type="submit" disabled={ registering || ! password || ( requireTotp && ! totpCode ) }>
				<FormattedMessage { ...( registering ? messages.registering : messages.confirm ) } />
			</Button>
			<Button type="button" variant="secondary" onClick={ onCancel } disabled={ registering }>
				<FormattedMessage { ...messages.cancel } />
			</Button>
		</div>
	</form>
);

PasskeyRegistrationForm.propTypes = {
	intl: intlShape.isRequired,
	name: PropTypes.string.isRequired,
	password: PropTypes.string.isRequired,
	totpCode: PropTypes.string.isRequired,
	requireTotp: PropTypes.bool.isRequired,
	registering: PropTypes.bool.isRequired,
	onName: PropTypes.func.isRequired,
	onPassword: PropTypes.func.isRequired,
	onTotpCode: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};

const PasskeyManagement = ( { intl } ) => {
	const { data, isLoading, reload } = useRequest( "passkeys" );
	const passkeys = Array.isArray( data ) ? data : [];

	const [ formOpen, setFormOpen ] = useState( false );
	const [ name, setName ] = useState( "Passkey" );
	const [ password, setPassword ] = useState( "" );
	const [ totpCode, setTotpCode ] = useState( "" );
	const [ requireTotp, setRequireTotp ] = useState( false );
	const [ registering, setRegistering ] = useState( false );
	const [ error, setError ] = useState( null );

	const resetForm = useCallback( () => {
		setFormOpen( false );
		setName( "Passkey" );
		setPassword( "" );
		setTotpCode( "" );
		setRequireTotp( false );
		setError( null );
	}, [] );

	const openForm = useCallback( () => {
		setError( null );
		setFormOpen( true );
	}, [] );

	const handleRegister = useCallback( async( event ) => {
		event.preventDefault();
		setError( null );
		setRegistering( true );
		try {
			await registerPasskey( { name: name || "Passkey", password, totpCode: totpCode || undefined } );
			resetForm();
			reload();
		} catch ( err ) {
			if ( err instanceof TotpRequiredError ) {
				setRequireTotp( true );
				setError( { message: err.message, type: "info" } );
			} else {
				setError( err );
			}
		} finally {
			setRegistering( false );
		}
	}, [ name, password, totpCode, reload, resetForm ] );

	const handleDelete = useCallback( async( id ) => {
		setError( null );
		try {
			await doRequest( prepareInternalRequest( `passkeys/${ id }`, "DELETE" ) );
			reload();
		} catch ( err ) {
			setError( { message: "Failed to delete passkey. Please try again.", type: "error" } );
		}
	}, [ reload ] );

	return (
		<div>
			<ErrorDisplay error={ error } />
			{ isLoading && <p><FormattedMessage id="passkeys.loading" defaultMessage="Loading passkeys..." /></p> }
			{ ! isLoading && passkeys.length === 0 && (
				<p><FormattedMessage { ...messages.noPasskeys } /></p>
			) }
			{ passkeys.length > 0 && (
				<div>
					{ passkeys.map( ( passkey ) => (
						<PasskeyRow
							key={ passkey.id }
							passkey={ passkey }
							onDelete={ handleDelete }
						/>
					) ) }
				</div>
			) }
			{ ! formOpen && (
				<div className="yst-mt-4">
					<Button variant="secondary" onClick={ openForm }>
						<FormattedMessage { ...messages.addPasskey } />
					</Button>
				</div>
			) }
			{ formOpen && (
				<PasskeyRegistrationForm
					intl={ intl }
					name={ name }
					password={ password }
					totpCode={ totpCode }
					requireTotp={ requireTotp }
					registering={ registering }
					onName={ ( e ) => setName( e.target.value ) }
					onPassword={ ( e ) => setPassword( e.target.value ) }
					onTotpCode={ ( e ) => setTotpCode( e.target.value ) }
					onSubmit={ handleRegister }
					onCancel={ resetForm }
				/>
			) }
		</div>
	);
};

PasskeyManagement.propTypes = {
	intl: intlShape.isRequired,
};

export default injectIntl( PasskeyManagement );
